import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PasswordModal.css';

export type PasswordModalMode = 'set' | 'verify' | 'change';

interface PasswordModalProps {
  mode: PasswordModalMode;
  onConfirm: (password: string) => void;
  onCancel: () => void;
  error?: string;
}

const PasswordModal: React.FC<PasswordModalProps> = ({ mode, onConfirm, onCancel, error }) => {
  const { t } = useTranslation();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState('');
  const [focusedButton, setFocusedButton] = useState<'cancel' | 'confirm' | null>(null);

  // focus management: refs for each field
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  // ref for the modal box — used for ArrowDown/Up navigation
  const boxRef = useRef<HTMLDivElement>(null);

  const getModalFocusables = (): HTMLElement[] => {
    if (!boxRef.current) return [];
    return Array.from(
      boxRef.current.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button:not([disabled])'
      )
    ).filter((el) => (el as HTMLElement).offsetParent !== null);
  };

  // focus first relevant field on mount
  useEffect(() => {
    if (mode === 'verify') currentRef.current?.focus();
    else if (mode === 'set') nextRef.current?.focus();
    else currentRef.current?.focus();
  }, [mode]);

  const handleSubmit = () => {
    setLocalError('');

    if (mode === 'verify') {
      if (!current) { setLocalError(t('parental.passwordPlaceholder')); return; }
      onConfirm(current);
      return;
    }

    if (mode === 'set') {
      if (!next) { setLocalError(t('parental.passwordPlaceholder')); return; }
      if (next !== confirm) { setLocalError(t('parental.passwordMismatch')); return; }
      onConfirm(next);
      return;
    }

    if (mode === 'change') {
      if (!current) { setLocalError(t('parental.oldPasswordPlaceholder')); return; }
      if (!next) { setLocalError(t('parental.newPasswordPlaceholder')); return; }
      if (next !== confirm) { setLocalError(t('parental.passwordMismatch')); return; }
      // Pack as "oldPass:::newPass" for the caller to split
      onConfirm(`${current}:::${next}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Stop events from bubbling to Settings (which has its own onKeyDown navigation).
    // This must always run — even if we don't handle the key ourselves.
    e.stopPropagation();

    const key = e.key;
    if (key === 'Escape' || key === 'GoBack' || key === 'BrowserBack') {
      e.preventDefault();
      onCancel();
      return;
    }
    if (key === 'Enter') {
      e.preventDefault();
      if (document.activeElement === cancelButtonRef.current) onCancel();
      else if (document.activeElement === confirmButtonRef.current || document.activeElement instanceof HTMLInputElement) handleSubmit();
      return;
    }
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      if (document.activeElement === cancelButtonRef.current || document.activeElement === confirmButtonRef.current) {
        e.preventDefault();
        (key === 'ArrowLeft' ? cancelButtonRef.current : confirmButtonRef.current)?.focus();
      }
      return;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault();
      const focusables = getModalFocusables();
      const idx = focusables.indexOf(document.activeElement as HTMLElement);
      if (key === 'ArrowDown') {
        focusables[Math.min(focusables.length - 1, idx + 1)]?.focus();
      } else {
        focusables[Math.max(0, idx - 1)]?.focus();
      }
    }
  };

  const displayError = error || localError;

  return (
    <div className="pwmodal__overlay" onKeyDown={handleKeyDown} tabIndex={-1}>
      <div ref={boxRef} className="pwmodal__box" role="dialog" aria-modal="true">
        <h2 className="pwmodal__title">
          {mode === 'set' && t('parental.addPassword')}
          {mode === 'verify' && t('parental.title')}
          {mode === 'change' && t('parental.changePassword')}
        </h2>

        {/* Current password field (verify + change modes) */}
        {(mode === 'verify' || mode === 'change') && (
          <input
            ref={currentRef}
            type="password"
            className="pwmodal__input"
            placeholder={mode === 'verify' ? t('parental.passwordPlaceholder') : t('parental.oldPasswordPlaceholder')}
            value={current}
            onChange={(e) => { setCurrent(e.target.value); setLocalError(''); }}
            autoComplete="current-password"
            inputMode="numeric"
          />
        )}

        {/* New password field (set + change modes) */}
        {(mode === 'set' || mode === 'change') && (
          <input
            ref={nextRef}
            type="password"
            className="pwmodal__input"
            placeholder={t('parental.newPasswordPlaceholder')}
            value={next}
            onChange={(e) => { setNext(e.target.value); setLocalError(''); }}
            autoComplete="new-password"
            inputMode="numeric"
          />
        )}

        {/* Confirm password field (set + change modes) */}
        {(mode === 'set' || mode === 'change') && (
          <input
            ref={confirmRef}
            type="password"
            className="pwmodal__input"
            placeholder={t('parental.confirmPasswordPlaceholder')}
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setLocalError(''); }}
            autoComplete="new-password"
            inputMode="numeric"
          />
        )}

        {displayError && <p className="pwmodal__error">{displayError}</p>}

        <div className="pwmodal__actions">
          <button
            ref={cancelButtonRef}
            className={`pwmodal__btn${focusedButton === 'cancel' ? ' pwmodal__btn--focused' : ''}`}
            onClick={onCancel}
            onFocus={() => setFocusedButton('cancel')}
            onBlur={() => setFocusedButton(null)}
            tabIndex={0}
          >
            {t('common.cancel')}
          </button>
          <button
            ref={confirmButtonRef}
            className={`pwmodal__btn${focusedButton === 'confirm' ? ' pwmodal__btn--focused' : ''}`}
            onClick={handleSubmit}
            onFocus={() => setFocusedButton('confirm')}
            onBlur={() => setFocusedButton(null)}
            tabIndex={0}
          >
            {t('common.confirm')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default PasswordModal;
